/** 来源：foodbuy/market Button；保留公共 variant/loading API，并吸收 market 的 loadingLabel。 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'ghostDanger' | 'icon' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'border border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-on-primary)] hover:bg-[var(--color-primary-hover)]',
  secondary: 'border border-[var(--color-line)] bg-[var(--color-panel)] text-[var(--color-ink)] hover:bg-[var(--color-soft)]',
  ghost: 'border border-transparent bg-transparent text-[var(--color-primary)] hover:bg-[var(--color-primary-soft)]',
  ghostDanger: 'border border-transparent bg-transparent text-[var(--color-danger)] hover:bg-[var(--color-danger-soft)]',
  icon: 'border border-transparent bg-transparent text-[var(--color-muted)] hover:bg-[var(--color-soft)] hover:text-[var(--color-ink)]',
  danger: 'border border-[var(--color-danger)] bg-[var(--color-danger)] text-[var(--color-on-primary)] hover:bg-[var(--color-danger-ink)]'
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 text-xs',
  md: 'px-3.5 text-sm',
  lg: 'px-5 text-sm'
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  loadingLabel?: string
  loadingMode?: 'label' | 'spinner'
  children: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, loadingLabel = '处理中…', loadingMode = 'label', className = '', children, disabled, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      className={`inline-flex min-h-[var(--control-height)] items-center justify-center gap-1.5 rounded-[var(--radius-md)] py-1.5 font-medium transition-colors focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:opacity-50 ${variantClasses[variant]} ${sizeClasses[size]} ${variant === 'icon' ? 'min-w-[var(--control-height)] !px-0' : ''} ${className}`.trim()}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && loadingMode === 'spinner' ? <span className="h-3.5 w-3.5 animate-spin rounded-[var(--radius-pill)] border-2 border-current border-r-transparent" aria-hidden="true" /> : null}
      {loading && loadingMode === 'label' ? loadingLabel : children}
    </button>
  )
})
